import React, { FC, useState, useCallback } from 'react';

import Table from 'molecules/Table';
import PaginationActions from 'molecules/Table/TablePagination/Actions';
import Link from 'atoms/Link/Link';

import {
  StyledCellContainer,
  StyledTableContainer,
} from 'molecules/Table/styles';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import InvoicePreview from 'molecules/InvoicePreview';

import { currencyFormatter } from 'utils/helpers/currencyFormatter';
import { palette } from 'utils/styles/variables';
import { months } from 'utils/constants/common';

import { Order } from 'organisms/InvoicesTable/types';

export interface ExpenseReportRow {
  id: string;
  project_id: number;
  project_name: string;
  company_hs_id: number;
  company_name: string;
  invoice_month: number;
  invoice_year: number;
  amount: number;
  currency: string;
  invoice_number: string;
  invoice_id: string;
  is_invoiced: boolean;
  frequency: string;
}

export type expenseReportSortBy =
  | 'project_name'
  | 'company_name'
  | 'created_at';

export const initialValue = {
  page: 1,
  limit: 20,
  sort_by: 'created_at',
  sort_order: 'desc',
};

const ExpenseReportTable: FC<{
  isCompany?: boolean;
  loading?: boolean;
  data: { expense_reports: ExpenseReportRow[]; total: number };
  onChange: (f: typeof initialValue) => void;
}> = ({
  isCompany,
  loading,
  data: { expense_reports = [], total = 0 },
  onChange,
}) => {
  const [pagination, setPagination] = useState(initialValue);
  const [invoicePreview, setInvoicePreview] = useState<string | null>(null);

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<expenseReportSortBy>('created_at');

  const handleRequestSort = useCallback(
    async (event: React.MouseEvent<unknown>, property: expenseReportSortBy) => {
      const isAsc = orderBy === property && order === 'asc';
      const nextState = {
        ...pagination,
        page: 1,
        sort_by: property,
        sort_order: isAsc ? 'desc' : 'asc',
      };

      onChange(nextState);

      setPagination(nextState);
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [pagination, orderBy, order, onChange]
  );

  const handleChangePage = useCallback(
    async (event: unknown, newPage: number) => {
      const nextState = {
        ...pagination,
        page: newPage + 1,
        sort_by: orderBy,
        sort_order: order,
      };

      onChange(nextState);
      setPagination(nextState);
    },
    [pagination, orderBy, order, onChange]
  );

  const handleChangeRowsPerPage = useCallback(
    async (option: number) => {
      const nextState = {
        limit: option,
        page: 1,
        sort_by: orderBy,
        sort_order: order,
      };

      onChange(nextState);
      setPagination(nextState);
    },
    [orderBy, order, pagination, onChange]
  );

  const handleClosePreview = useCallback(() => {
    setInvoicePreview(null);
    document.body.setAttribute('id', '');
  }, []);

  const columns = [
    {
      key: 'created_at',
      label: 'Month',
      sortable: false,
      width: '10%',
      render: ({ invoice_month, invoice_year }: ExpenseReportRow) => (
        <StyledCellText $textType="bold">
          {months[invoice_month - 1]}, {invoice_year}
        </StyledCellText>
      ),
    },
    {
      key: 'project_name',
      label: 'Project Name',
      sortable: true,
      width: '20%',
      render: ({ project_name }: ExpenseReportRow) => (
        <StyledCellText>{project_name}</StyledCellText>
      ),
    },
    ...(isCompany
      ? []
      : [
          {
            key: 'company_name',
            label: 'Company Name',
            sortable: true,
            width: '20%',
            render: ({ company_name, company_hs_id }: ExpenseReportRow) => (
              <Link to={`/company/${company_hs_id}`} $textDecoration="none">
                <StyledCellText $textType="link">{company_name}</StyledCellText>
              </Link>
            ),
          },
        ]),
    {
      key: 'frequency',
      label: 'Frequency',
      sortable: false,
      width: '15%',
      render: ({ frequency }: ExpenseReportRow) => (
        <StyledCellContainer>
          <StyledCellText>{frequency ?? ''}</StyledCellText>
        </StyledCellContainer>
      ),
    },
    {
      key: 'invoice_number',
      label: 'Invoice #',
      sortable: false,
      width: '10%',
      render: ({ invoice_number, invoice_id }: ExpenseReportRow) =>
        invoice_number && (
          <StyledCellText
            $color={palette.primary.main}
            sx={{
              display: 'inline',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={(event) => {
              event?.preventDefault();
              event?.stopPropagation();

              setInvoicePreview(invoice_id);
              document.body.setAttribute('id', 'body');
            }}
          >
            #{invoice_number}
          </StyledCellText>
        ),
    },
    {
      key: 'amount',
      label: 'Amount',
      sortable: false,
      width: '11%',
      render: ({ amount, currency }: ExpenseReportRow) => (
        <StyledCellContainer $hStack $right>
          <StyledCellText>
            {amount ? currencyFormatter(amount, currency) : ''}
          </StyledCellText>
        </StyledCellContainer>
      ),
    },
  ];

  return (
    <StyledTableContainer>
      <Table
        data={expense_reports}
        columns={columns}
        idKey="id"
        sx={{ minWidth: 650 }}
        aria-label="simple table"
        loading={loading}
        order={order}
        orderBy={orderBy}
        onRequestSort={handleRequestSort}
      />
      <StyledTablePagination
        labelDisplayedRows={() => null}
        rowsPerPageOptions={[]}
        count={total}
        rowsPerPage={pagination.limit}
        page={total ? pagination.page - 1 : 0}
        onPageChange={handleChangePage}
        ActionsComponent={(props) => (
          <PaginationActions
            {...props}
            labelRowsPerPage="Items per page"
            rowsPerPageOptions={[20, 50, 100]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      />

      {invoicePreview && (
        <InvoicePreview id={invoicePreview} onClose={handleClosePreview} />
      )}
    </StyledTableContainer>
  );
};

export default ExpenseReportTable;
